import * as Sentry from '@sentry/react';
import {
  Environment,
  Network,
  RecordSource,
  Store,
  type FetchFunction,
  type IEnvironment,
} from 'relay-runtime';

const endpoint =
  import.meta.env.VITE_GRAPHQL_ENDPOINT ?? `${window.location.origin}/graphql`;

/**
 * Create a Relay fetch function with optional auth token
 */
function createFetchFunction(getAccessToken: () => string | null): FetchFunction {
  return async (operation, variables) => {
    const token = getAccessToken();

    // Build headers with optional trace context propagation
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Propagate trace context to backend if Sentry span is active
    const span = Sentry.getActiveSpan();
    if (span) {
      const spanContext = span.spanContext();
      headers.traceparent = `00-${spanContext.traceId}-${spanContext.spanId}-01`;
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query: operation.text,
        variables,
      }),
    });

    if (!response.ok) {
      throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  };
}

/**
 * Create a Relay Environment instance
 */
export function createRelayEnvironment(
  getAccessToken: () => string | null = () => null
): IEnvironment {
  return new Environment({
    network: Network.create(createFetchFunction(getAccessToken)),
    store: new Store(new RecordSource(), {
      // Garbage collect records after 5 minutes
      gcReleaseBufferSize: 10,
    }),
  });
}

/**
 * Default environment for unauthenticated requests
 */
let defaultEnvironment: IEnvironment | null = null;

export function getDefaultEnvironment(): IEnvironment {
  if (!defaultEnvironment) {
    defaultEnvironment = createRelayEnvironment();
  }
  return defaultEnvironment;
}

/**
 * Reset the default environment (useful for testing)
 */
export function resetDefaultEnvironment(): void {
  defaultEnvironment = null;
}
